import { EMPTY_FIELD } from 'constants/emptyField';

import CopyAllIcon from '@mui/icons-material/CopyAll';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { JsonEditor, JsonEditorProps, monoLightTheme } from 'json-edit-react';
import { FieldProps, useRecordContext } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { IconButton } from 'shared/mui/IconButtons';
import { Tooltip } from 'shared/mui/Tooltip';

export type JsonCopyToClipboardFieldProps = Omit<FieldProps, 'source'> &
  Partial<JsonEditorProps> & {
    src?: Record<string, any>;
    source?: string;
    containerStyle?: React.CSSProperties;
  };

const jsonValueColor = 'rgba(25, 118, 210, 1)';

const baseTheme = {
  container: {
    backgroundColor: '#fff',
    fontFamily: 'Roboto, monospace',
    fontSize: '16px',
    border: '1px solid rgba(0, 0, 0, 0.08)',
  },
  string: jsonValueColor,
  number: jsonValueColor,
  boolean: { color: jsonValueColor, fontStyle: 'normal' },
  null: {
    color: jsonValueColor,
    fontVariant: 'normal',
    fontWeight: 'normal',
  },
  iconCollection: 'rgb(0, 43, 54)',
  iconEdit: 'edit',
  iconDelete: 'rgb(203, 75, 22)',
  iconAdd: 'green',
  iconCopy: 'rgb(38, 139, 210)',
  iconOk: 'green',
  iconCancel: 'rgb(203, 75, 22)',
};

export const JsonCopyToClipboardField = ({
  source,
  src,
  containerStyle,
  ...jsonEditorProps
}: JsonCopyToClipboardFieldProps) => {
  const record = useRecordContext();

  const jsonValue = src || record?.[source as string];
  const { tooltipText, handleCopy } = useCopyToClipboard(JSON.stringify(jsonValue, null, 2));

  if (!jsonValue) {
    return <span>{EMPTY_FIELD}</span>;
  }

  const isEmptyJson = !Object.keys(jsonValue).length;

  const theme = [
    monoLightTheme,
    {
      ...baseTheme,
      container: {
        ...baseTheme.container,
        ...containerStyle,
      },
    },
  ] as JsonEditorProps['theme'];

  return (
    <Box sx={{ position: 'relative' }}>
      <JsonEditor
        {...jsonEditorProps}
        data={jsonValue}
        restrictAdd={true}
        restrictDelete={true}
        restrictEdit={true}
        theme={theme}
      />
      <Tooltip
        PopperProps={{
          disablePortal: true,
        }}
        open={!!tooltipText}
        sx={{ position: 'absolute', top: '8px', right: '8px' }}
        title={tooltipText}
      >
        <IconButton disabled={isEmptyJson} onClick={handleCopy}>
          <CopyAllIcon color={isEmptyJson ? 'secondary' : 'primary'} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
